<template>
  <div class="store-view bg-white" :style="`${theme && theme.type == 'store' ? 'height: calc(100% - 65px)' : 'height:100%'}`">
    <div class="store-page h-100 overflow-auto">
      <UnAuthorizedAccess
        v-if="!company && !loggedIn"
        class="m-0 w-100 rounded-0"
        title="Sorry, you are not authorized to view this menu."
        message="Sorry there was an error. You may have some cached data
                      from another account. Please try logging into your
                      account and trying again."
      />
      <UnAuthorizedAccess
        v-else-if="!company && loggedIn"
        title="Sorry, you are not authorized to view this menu."
        message="If someone other than the seller shared this link with
                    you, this is a private link that does not belong to you.
                    You should contact the seller so they can set you up as
                    a buyer in their account and have them send you your
                    unique link."
        class="m-0 w-100 rounded-0"
      />
      <div v-else-if="buyer && buyer.hold" class="p-4 alert alert-warning text-dark m-0 text-center">
        <h5 class="m-0">
          <i class="fas fa-exclamation-triangle text-danger" />
          <span v-if="buyer && buyer.hold && !buyer.hold_message" class="ml-2">
            Your account is currently on hold. Please contact the seller for more information.
          </span>
          <span v-if="buyer && buyer.hold && buyer.hold_message" class="ml-2">
            {{ buyer.hold_message }}
          </span>
        </h5>
      </div>

      <div class="store-content m-0 w-100" :style="`height: ${theme && theme.page_sidebar_visible ? '100%' : '100%'} `">
        <div style="min-height: 100%" class="row m-0 p-0 w-100">
          <StoreFrontSidebar
            v-if="!showingContactSales"
            :key="sidebarKey"
            :width="sidebarWidth"
            :company="company"
            :categories="filterCategoryItems"
            :product-types="filterProductTypesItems"
            :strains="filterStrainsItems"
            :brands="filterBrandsItems"
            :cannabinoids="filterCannabinoidsItems"
            :features="filterFeaturedItems"
            :price-ranges="filterPriceRangesItems"
            :availabilty="filterAvailabiltyItem"
            :industries="filterIndustryItems"
            :category="selectedCategory"
            :products="products"
            :filtered-products="filteredProducts"
            :hide-prices="hidePrices"
            :theme="theme"
            :filters="filters"
            :visible="showSidebar"
            :cart-visible="cartVisible"
            :layout="defaultLayout"
            @hide-sidebar="showSidebar = false"
            @category-selected="onCategorySelected"
            @product-type-selected="onProductTypeSelected"
            @strain-selected="onStrainSelected"
            @brand-selected="onBrandSelected"
            @industry-selected="onIndustrySelected"
            @cannabinoid-selected="onCannabinoidSelected"
            @price-range-selected="onPriceRangeSelected"
            @pre-order-selected="onPreOrderSelected"
            @pre-order-sub-item-selected="onPreOrderSubItemSelected"
            @featured-selected="onFeaturedProductSelected"
            @potency-selected="onPotencySelected"
          />

          <div :style="backgroundStyle" class="store-products-container col p-0 m-0 overflow-hidden">
            <StoreFrontBanner :theme="theme" />
            <div :style="gradientStyle" class="h-100">
              <BushelAlert v-if="company.block_buyer_order && !isTheSeller && buyerUuid" alert-class="alert-info" class="m-3 text-center">
                <h5>
                  Heads up. This seller requires that you contact them to place orders. Either give your rep a call, or use the live chat above.
                </h5>
              </BushelAlert>
              <FeaturedProductBanner
                v-if="!cartVisible && !showingContactSales"
                :products="products"
                :theme="theme"
                :sidebar-showing="showSidebar"
                :hide-prices="hidePrices"
                @view-product="launchProductDisplayPageModal"
              />
              <StoreFrontToolbar
                v-if="!cartVisible && theme && theme.page_toolbar_visible"
                :theme="theme"
                :company="company"
                :pricing-tier-discounts="pricingTierDiscounts"
                :bulk-discounts="bulkDiscounts"
                :hide-shopping-actions="hideShoppingActions"
                :is-table-view="defaultLayout == 'table'"
                :hide-prices="hidePrices"
                :sidebar-visible="showSidebar"
                :sidebar-toggle-visible="theme.page_sidebar_visible && categories.length > 1 && defaultLayout == 'grid'"
                :showing-contact-sales="showingContactSales"
                :show-contact-sales-btn="showContactSalesBtn"
                :layout="defaultLayout"
                :selected-sort="selectedSortBy"
                :is-the-seller="isTheSeller"
                @sort-by="sortBy"
                @sidebar-toggle="
                  () => {
                    showSidebar = showSidebar ? false : true;
                  }
                "
                @layout-toggle-grid="switchToGrid"
                @layout-toggle-table="switchToTable"
                @show-company-display-page="isShowingCompanyDisplayPageModal = true"
                @show-bulk-discounts-modal="isShowingBulkDiscountModal = true"
                @show-price-tier-discounts-modal="isShowingPricingTierDiscountsModal = true"
                @hide-contact-sales="$emit('hide-contact-sales', true)"
                @show-contact-sales="$emit('show-contact-sales', true)"
              />
              <div v-if="!cartVisible && !showingContactSales && company && products && theme">
                <div v-if="defaultLayout == 'grid'" class="show-grid grid p-0 px-sm-3">
                  <StoreGrid
                    :logged-in="loggedIn"
                    :company="company"
                    :user="user"
                    :buyer="buyer"
                    :cart-items="cartItems"
                    :active-location="activeCart"
                    :category="selectedCategory"
                    :filtered="filtered"
                    :bulk-discounts="bulkDiscounts"
                    :use-cannabis-spinner="useCannabisSpinner"
                    :chat-enabled="false"
                    :is-the-seller="isTheSeller"
                    :sidebar-visible="showSidebar && theme.page_sidebar_visible"
                    :products="filteredProducts"
                    :hide-prices="hidePrices"
                    :hide-quantity="hideQuantity"
                    :hide-shopping-actions="hideShoppingActions"
                    :theme="theme"
                    :hide-branding="hideBranding"
                    :current-page="currentPage"
                    @contact-button-clicked="$emit('contactButtonClicked')"
                    @add-to-cart="(product) => $emit('add-to-cart', product)"
                    @switch-page="switchPage"
                  />
                  <StoreFrontFooter :company="company" :theme="theme" />
                </div>
                <div v-else-if="defaultLayout == 'table'" class="px-3 pb-2 table-layout show-table-layout">
                  <div>
                    <StorefrontTable
                      class="bg-white"
                      table-class="bg-white rounded m-0"
                      :chat-enabled="false"
                      :user="user"
                      :buyer="buyer"
                      :limit-override="100"
                      :storefront-company="company"
                      :show-med-rec-designations="company.state && company.state.display_med_rec_designations ? true : false"
                      :cart-items="cartItems"
                      :active-location="activeCart"
                      :products="products"
                      :use-cannabis-spinner="useCannabisSpinner"
                      :bulk-discounts="bulkDiscounts"
                      :theme="theme"
                      :is-the-seller="isTheSeller"
                      :product-categories="categories"
                      :hide-powered-by-logo="hideBranding"
                      :table-shadow="theme.page_product_layout_box_shadow_visible"
                      :hide-prices="hidePrices"
                      :hide-quantities="hideQuantity"
                      :disable-add="hideShoppingActions"
                      @add-to-cart="(product) => $emit('add-to-cart', product)"
                      @contact-button-clicked="pdpContactClicked"
                    />
                  </div>
                  <div class="col-12">
                    <StoreFrontFooter :company="company" :theme="theme" />
                  </div>
                </div>
              </div>
              <div v-else-if="cartVisible && !hideShoppingActions && !showingContactSales" style="min-height: 800px" class="pt-4 pb-5 px-5">
                <Cart
                  :key="cartKey"
                  :created-order="createdOrder"
                  :dispatching-order="dispatchingOrder"
                  class=""
                  :cart-options="cartOptions"
                  :storefront-company="company"
                  :active-cart="activeCart"
                  :cart-items="cartItems"
                  :cart-minimums="cartMinimums"
                  :loading-cart="loadingCart"
                  :order-groups="orderGroups"
                  :buyer-data="buyer"
                  :logged-in="loggedIn"
                  :is-the-seller="isTheSeller"
                  @start-sneaky-login="$emit('show-login', true)"
                  @start-sneaky-register="(val) => $emit('show-register', val)"
                  @create-order="createOrder"
                  @back-to-storefront="$emit('hide-cart')"
                  @back-to-storefront-and-fetch-items="$emit('hide-cart')"
                  @back-to-account="$router.push({ path: '/' })"
                  @view-orders="$router.push({ path: '/orders' })"
                  @fetch-cart="(buyerUuid) => $emit('fetch-cart', buyerUuid)"
                  @remove-from-cart="(item) => $emit('remove-from-cart', item)"
                  @handle-update-quantity="(val) => $emit('handleUpdateQuantity', val)"
                  @adjust-to-max-quantity-in-cart="(item) => $emit('adjust-to-max-quantity-in-cart', item)"
                />
              </div>
              <div v-if="showingContactSales" class="w-75 mx-auto mt-3 px-3 pb-4">
                <Contact
                  class="bg-white rounded p-5"
                  :company="company"
                  :theme="theme"
                  :buyer="buyer"
                  :user="user"
                  :is-store-front="isStoreFront"
                  @hide-contact-sales="$emit('hide-contact-sales', true)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <CompanyDisplay
      v-if="company && company.id && isShowingCompanyDisplayPageModal"
      :theme="theme"
      :show="isShowingCompanyDisplayPageModal"
      :company-id="company.id"
      @hide="isShowingCompanyDisplayPageModal = false"
    />
    <BulkDiscountModal :show="isShowingBulkDiscountModal" :bulk-discounts="bulkDiscounts" @hide="isShowingBulkDiscountModal = false" />
    <PricingTierDiscountsModal
      :show="isShowingPricingTierDiscountsModal"
      :pricing-tier-discounts="pricingTierDiscounts"
      @hide="isShowingPricingTierDiscountsModal = false"
    />
    <SneakyRegisterModal
      v-if="buyerUuid"
      :key="registerModalKey"
      :show="registerVisible"
      :invitation-email="invitationEmail"
      :buyer-uuid="buyerUuid"
      registration-success-message="Registered Successfully"
      @registered="registeredSuccessfully"
      @hide="$emit('hide-register', true)"
      @view-login="$emit('show-login', true)"
    />
    <SneakyLoginModal
      v-if="buyerUuid"
      :key="loginModalKey"
      :buyer-data="buyer"
      :show="loginVisible"
      :invitation-email="invitationEmail"
      :buyer-uuid="buyerUuid"
      :no-reload-after-auth="noReloadAfterAuth"
      @registered="$emit('reload')"
      @hide="$emit('hide-login', true)"
    />
    <PTOModal
      :key="ptoModalKey"
      :show="isShowingPTOModal"
      :product="ptoProduct"
      :batch="ptoBatch"
      @hide="isShowingPTOModal = false"
      @create-pack-to-order-items="createPackToOrderItems"
    />
    <ProductDisplayPageModal
      v-if="selectedProduct"
      :key="productDisplayPageModalKey"
      :company="company"
      :show="showProductModal !== false"
      :buyer-id="buyer ? buyer.id : null"
      :selected-product="selectedProduct"
      :bulk-discounts="productCurrentlyShowingBulkDiscounts"
      :selected-batch-id="selectedBatchId"
      :hide-prices="hidePrices"
      :hide-quantities="hideQuantity"
      :disable-add="hideShoppingActions"
      :use-cannabis-spinner="useCannabisSpinner"
      @hide="hideProductDisplayPageModal"
      @added-product="addProductFromDisplayPage"
      @contact-button-clicked="pdpContactClicked"
      @launch-p-t-o-modal="handleLaunchingPTOModalFromPDPModal"
    />
  </div>
</template>
<script>
import clonedeep from "lodash.clonedeep";
import dayjs from "dayjs";
import StoreGrid from "./layout/StoreGrid.vue";
import StoreFrontBanner from "./layout/StoreFrontBanner.vue";
import UnAuthorizedAccess from "./components/UnAuthorizedAccess.vue";
import StorefrontTable from "@/components/storefront/StorefrontTable.vue";
import StoreFrontSidebar from "./layout/StoreFrontSidebar.vue";
import StoreFrontToolbar from "./layout/StoreFrontToolbar.vue";
import StoreFrontFooter from "./layout/StoreFrontFooter.vue";
import Cart from "./Cart.vue";
import BulkDiscountModal from "@/components/storefront/BulkDiscountModal.vue";
import PricingTierDiscountsModal from "./PricingTierDiscountsModal.vue";
import SneakyRegisterModal from "@/components/auth/SneakyRegisterModal.vue";
import SneakyLoginModal from "./SneakyLoginModal.vue";
import Contact from "./components/Contact.vue";
import CompanyDisplay from "@/components/companyDisplay/CompanyDisplay.vue";
import PTOModal from "@/components/storefront/PTOModal.vue";
import ProductDisplayPageModal from "@/components/productDisplay/ProductDisplayPageModal.vue";
import FeaturedProductBanner from "@/components/storefront/featuredProduct/FeaturedProductBanner.vue";
import { navTo } from "@/services/helpers";
import BushelAlert from "@/components/common/BushelAlert.vue";

export default {
  name: "StoreView",
  components: {
    StoreGrid,
    StoreFrontBanner,
    UnAuthorizedAccess,
    StorefrontTable,
    StoreFrontSidebar,
    StoreFrontToolbar,
    Cart,
    BulkDiscountModal,
    PricingTierDiscountsModal,
    SneakyRegisterModal,
    SneakyLoginModal,
    StoreFrontFooter,
    Contact,
    CompanyDisplay,
    PTOModal,
    ProductDisplayPageModal,
    FeaturedProductBanner,
    BushelAlert,
  },
  props: {
    user: { type: Object },
    company: { type: Object, required: true },
    products: { type: Array, required: true },
    categories: { type: Array, required: true },
    theme: { type: Object, required: true },
    invitationEmail: { type: String },
    loggedIn: { type: Boolean },
    buyerUuid: { type: String },
    cartItems: { type: Array },
    orderGroups: { type: Array },
    cartMinimums: { type: Object },
    bulkDiscounts: { type: Array },
    pricingTierDiscounts: { type: Object },
    loadingCart: { type: Boolean },
    buyer: { type: Object },
    dispatchingOrder: { type: Boolean },
    createdOrder: { type: Boolean },
    isTheSeller: { type: Boolean },
    cartVisible: { type: Boolean },
    loginVisible: { type: Boolean },
    hideBranding: { type: Boolean, default: false },
    registerVisible: { type: Boolean },
    hidePrices: { type: Boolean, default: false },
    hideQuantity: { type: Boolean, default: false },
    hideShoppingActions: { type: Boolean, required: true },
    registerModalKey: { type: Number },
    loginModalKey: { type: Number },
    noReloadAfterAuth: { type: Boolean, default: false },
    activeView: { type: String },
    showingContactSales: { type: Boolean, default: false },
    showContactSalesBtn: { type: Boolean, default: false },
    isStoreFront: { type: Boolean, default: false },
    cartOptions: { type: Array },
    activeCart: { type: Object },
    showAuthModal: { type: Boolean, default: false },
  },
  emits: [
    "hide-login",
    "hide-register",
    "show-login",
    "show-register",
    "fetch-cart",
    "add-to-cart",
    "handleUpdateQuantity",
    "adjust-to-max-quantity-in-cart",
    "contactButtonClicked",
    "switch-page",
    "hide-cart",
    "show-contact-sales",
    "hide-contact-sales",
    "reload",
    "remove-from-cart",
    "view-switched",
  ],
  data() {
    return {
      isShowingPTOModal: false,
      ptoModalKey: 52535,
      ptoProduct: {},
      ptoBatch: {},
      productDisplayPageModalKey: 1,
      showProductModal: false,
      productCurrentlyShowingBulkDiscounts: [],
      selectedBatchId: null,
      selectedProduct: null,
      isShowingCompanyDisplayPageModal: false,
      selectedSortBy: { id: 1, name: "Name" },
      isShowingBulkDiscountModal: false,
      isShowingPricingTierDiscountsModal: false,
      selectedCategory: "All Products",
      showSidebar: window.innerWidth > 992 && this.categories.length > 1,
      useCannabisSpinner: true,
      showStoreReg: false,
      filters: [],
      filteredProducts: [],
      filtered: false,
      cartKey: 1,
      filtersApplied: [],
      currentPage: 1,
      sidebarKey: 999999,
      sidebarWidth: "300px",
      defaultLayout: this.activeView ? this.activeView.toLowerCase() : this.theme.page_product_layout_default.toLowerCase(),
      filterPotency: null,
      filterPotencyUnit: null,
      filterCategoryItems: [],
      filterProductTypesItems: [],
      filterStrainsItems: [],
      filterBrandsItems: [],
      filterCannabinoidsItems: [],
      filterPriceRangesItems: [],
      filterIndustryItems: [
        { label: "Medical", value: "Medical", selected: false },
        { label: "Recreational", value: "Recreational", selected: false },
      ],
      filterFeaturedItems: [{ label: "Featured Products", value: true, selected: false }],
      filterAvailabiltyItem: [
        {
          label: "In-Stock",
          value: "in-stock",
          selected: false,
        },
        {
          label: "Pre Order",
          value: "Pre Order",
          selected: false,
          subItems: [
            {
              label: "Available within 1 Week",
              value: "Available within 1 Week",
              selected: false,
            },
            {
              label: "Available within 2 Weeks",
              value: "Available within 2 Weeks",
              selected: false,
            },
            {
              label: "Available within 1 Month",
              value: "Available within 1 Month",
              selected: false,
            },
            {
              label: "Available within 3 Months",
              value: "Available within 3 Months",
              selected: false,
            },
          ],
        },
      ],
    };
  },

  computed: {
    gradientStyle() {
      return this.theme && this.theme.page_gradient_background
        ? `background: linear-gradient( ${
            this.theme && this.theme.page_background_color ? this.theme.page_background_color : ""
          } 10%, rgba(0,0,0,0.3))`
        : null;
    },
    featuredProducts() {
      return this.products.filter((product) => product.featured);
    },
    backgroundStyle() {
      const bgStyle = {
        backgroundColor: this.theme ? this.theme.page_background_color : "",
      };

      if (this.theme && this.theme.page_background_image) {
        bgStyle.backgroundImage = `url(${this.theme ? this.theme.page_background_image : ""})`;
        bgStyle.backgroundSize = "cover";
        bgStyle.backgroundPosition = "center";
      }

      return bgStyle;
    },
  },
  watch: {
    theme(n, o) {
      if (this.activeView) {
        this.defaultLayout = this.activeView.toLowerCase();
      } else {
        this.defaultLayout = this.theme.page_product_layout_default.toLowerCase();
      }
    },
    products(n, o) {
      this.handleLoadedProducts(n);
    },
  },
  mounted() {
    this.handleLoadedProducts(this.products);
    this.onPageResize();
    this.buildCategoryFilterItems();
    this.buildStrainsFilterItems(this.products);
    this.buildBrandsFilterItems(this.products);
    this.buildCannabinoidsFilterItems(this.products);
    this.buildPriceRangesFilterItems(this.products);
  },
  methods: {
    navTo,
    registeredSuccessfully() {
      if (this.noReloadAfterAuth) {
        this.$emit("hide-register", true);
      } else {
        this.$emit("hide-register", true);
        this.$emit("reload");
      }
    },
    buildCategoryFilterItems() {
      this.categories.forEach((category) => {
        if (category.id !== "default" && !this.filterCategoryItems.some((item) => item.name === category.name)) {
          this.filterCategoryItems.push({
            label: category.long_display_name,
            value: category.name,
            selected: false,
            types: this.buildProductTypesFilterItems(category.name),
          });
        }
      });
    },
    buildProductTypesFilterItems(category) {
      const filterProductTypesItems = [];
      this.products.forEach((product) => {
        if (category === product.category_name && product.product_type && !filterProductTypesItems.some((i) => i.value == product.product_type)) {
          filterProductTypesItems.push({
            label: product.product_type,
            value: product.product_type,
            selected: false,
          });
        }
      });

      return filterProductTypesItems;
    },
    buildStrainsFilterItems(products) {
      const foundStrains = [];
      this.products.forEach((product) => {
        if (
          product.cultivar_type &&
          !this.filterStrainsItems.some((i) => i.value == product.cultivar_type) &&
          !foundStrains.some((i) => i.value == product.cultivar_type)
        ) {
          foundStrains.push({
            label: product.cultivar_type,
            value: product.cultivar_type,
            selected: false,
          });
        } else if (
          product.cultivar_type &&
          this.filterStrainsItems.some((i) => i.value == product.cultivar_type) &&
          !foundStrains.some((i) => i.value == product.cultivar_type)
        ) {
          const foundStrain = this.filterStrainsItems.find((b) => b.value == product.cultivar_type);
          foundStrains.push({
            label: product.cultivar_type,
            value: product.cultivar_type,
            selected: foundStrain.selected,
          });
        }
      });

      this.filterStrainsItems = foundStrains;
    },
    buildBrandsFilterItems(products) {
      const foundBrands = [];
      products.forEach((product) => {
        if (
          product.brand &&
          !this.filterBrandsItems.some((i) => i.value == product.brand.name) &&
          !foundBrands.some((i) => i.value == product.brand.name)
        ) {
          foundBrands.push({
            label: product.brand.name,
            value: product.brand.name,
            selected: false,
          });
        } else if (
          product.brand &&
          this.filterBrandsItems.some((i) => i.value == product.brand.name) &&
          !foundBrands.some((i) => i.value == product.brand.name)
        ) {
          const foundBrand = this.filterBrandsItems.find((b) => b.value == product.brand.name);
          foundBrands.push({
            label: product.brand.name,
            value: product.brand.name,
            selected: foundBrand.selected,
          });
        }
      });
      this.filterBrandsItems = foundBrands;
    },
    buildCannabinoidsFilterItems(products) {
      const foundCannabinoids = [];
      products.forEach((product) => {
        if (
          product.predominateCanabinoidAbbreviation &&
          !this.filterCannabinoidsItems.some((i) => i.value === product.predominateCanabinoidAbbreviation) &&
          !foundCannabinoids.some((i) => i.value == product.predominateCanabinoidAbbreviation)
        ) {
          foundCannabinoids.push({
            label: product.predominateCanabinoidAbbreviation,
            value: product.predominateCanabinoidAbbreviation,
            selected: false,
          });
        } else if (
          product.predominateCanabinoidAbbreviation &&
          this.filterCannabinoidsItems.some((i) => i.value == product.predominateCanabinoidAbbreviation) &&
          !foundCannabinoids.some((i) => i.value == product.predominateCanabinoidAbbreviation)
        ) {
          const foundCannabinoid = this.filterCannabinoidsItems.find((b) => b.value === product.predominateCanabinoidAbbreviation);
          foundCannabinoids.push({
            label: product.predominateCanabinoidAbbreviation,
            value: product.predominateCanabinoidAbbreviation,
            selected: foundCannabinoid.selected,
          });
        }
      });

      this.filterCannabinoidsItems = foundCannabinoids;
    },
    buildPriceRangesFilterItems(products) {
      const maxPrice = this.getMaxPriceHelper(products);
      const foundPriceRanges = [];

      const chunk = Math.floor(maxPrice / 6);
      if (chunk >= 1 && products.length > 1) {
        for (let i = 0; i < maxPrice; i += chunk) {
          let label = null;
          let range = null;

          if (i == 0) {
            label = `Under $${i + chunk}`;
            range = [null, i + chunk];
          } else if (i + chunk < maxPrice) {
            label = `$${i} to $${i + chunk}`;
            range = [i, i + chunk];
          } else {
            label = `$${i} and over`;
            range = [i, null];
          }

          if (!this.filterPriceRangesItems.some((r) => r.label === label) && !foundPriceRanges.some((r) => r.label === label)) {
            foundPriceRanges.push({
              label,
              value: range,
              selected: false,
            });
          } else if (this.filterPriceRangesItems.some((r) => r.label == label) && !foundPriceRanges.some((r) => r.label == label)) {
            const foundPriceRange = this.filterPriceRangesItems.find((r) => r.label == label);
            foundPriceRanges.push({
              label,
              value: range,
              selected: foundPriceRange.selected,
            });
          }
        }

        this.filterPriceRangesItems = foundPriceRanges;
      }
    },

    getMaxPriceHelper(products) {
      let maxPrice = 0;

      products.forEach((product) => {
        const price = product.listing_price;
        if (price > maxPrice) {
          maxPrice = price;
        }
      });

      return maxPrice / 100;
    },
    onCategorySelected(item) {
      this.selectedCategory = "All Products";
      const newData = this.filterCategoryItems.map((i) => {
        const rObj = { ...i };
        if (rObj.value == item.value) {
          rObj.selected = !rObj.selected;
          rObj.types = this.buildProductTypesFilterItems(rObj.value);
        }

        return rObj;
      });
      this.filterCategoryItems = newData;
      this.filterProducts();
    },

    onProductTypeSelected(item) {
      const newData = this.filterCategoryItems.map((i) => {
        const rObj = { ...i };
        if (rObj.value === item.category.value) {
          const newTypes = rObj.types.map((t) => {
            const tObj = { ...t };
            if (tObj.value == item.type.value) {
              tObj.selected = !tObj.selected;
            }
            return tObj;
          });
          rObj.types = newTypes;
        }
        return rObj;
      });
      this.filterCategoryItems = newData;
      this.filterProducts();
    },
    onStrainSelected(item) {
      const nd = this.filterStrainsItems.map((i) => {
        const rObj = { ...i };
        if (rObj.value !== item.value) {
          rObj.selected = false;
        } else {
          rObj.selected = !rObj.selected;
        }
        return rObj;
      });
      this.filterStrainsItems = nd;
      this.filterProducts();
    },
    onBrandSelected(item) {
      const nd = this.filterBrandsItems.map((i) => {
        const rObj = { ...i };
        if (rObj.value !== item.value) {
          rObj.selected = false;
        } else {
          rObj.selected = !rObj.selected;
        }
        return rObj;
      });
      this.filterBrandsItems = nd;
      this.filterProducts();
    },
    onIndustrySelected(item) {
      const nd = this.filterIndustryItems.map((i) => {
        const rObj = { ...i };
        if (rObj.value !== item.value) {
          rObj.selected = false;
        } else {
          rObj.selected = !rObj.selected;
        }
        return rObj;
      });
      this.filterIndustryItems = nd;
      this.filterProducts();
    },
    onFeaturedProductSelected(item) {
      const nd = this.filterFeaturedItems.map((i) => {
        const rObj = { ...i };
        if (rObj.value !== item.value) {
          rObj.selected = false;
        } else {
          rObj.selected = !rObj.selected;
        }
        return rObj;
      });
      this.filterFeaturedItems = nd;
      this.filterProducts();
    },
    onCannabinoidSelected(item) {
      const nd = this.filterCannabinoidsItems.map((i) => {
        const rObj = { ...i };
        if (rObj.value !== item.value) {
          rObj.selected = false;
        } else {
          rObj.selected = !rObj.selected;
        }
        return rObj;
      });
      this.filterCannabinoidsItems = nd;
      this.filterProducts();
    },
    onPriceRangeSelected(item) {
      if (item.label == "custom") {
        this.filterProducts(item);
        return false;
      }
      const nd = this.filterPriceRangesItems.map((i) => {
        const rObj = { ...i };
        if (rObj.value !== item.value) {
          rObj.selected = false;
        } else {
          rObj.selected = !rObj.selected;
        }
        return rObj;
      });
      this.filterPriceRangesItems = nd;
      this.filterProducts();
      return false;
    },
    onPreOrderSelected(item) {
      const nd = this.filterAvailabiltyItem.map((i) => {
        const rObj = { ...i };
        if (rObj.value !== item.value) {
          rObj.selected = false;
        } else {
          rObj.selected = !rObj.selected;
        }
        if (rObj.value == "Pre Order" && rObj.selected == false) {
          if (rObj.subItems) {
            const sI = rObj.subItems.map((sub) => {
              const subObj = { ...sub };
              if (subObj.value !== item.value) {
                subObj.selected = false;
              }
              return subObj;
            });
            rObj.subItems = sI;
          }
        }

        return rObj;
      });

      this.filterAvailabiltyItem = nd;

      this.filterProducts();
    },
    onPreOrderSubItemSelected(item) {
      const nd = this.filterAvailabiltyItem.map((i) => {
        const rObj = { ...i };
        if (rObj.value !== "Pre Order") {
          rObj.selected = false;
        } else {
          rObj.selected = true;
          if (rObj.subItems) {
            const sI = rObj.subItems.map((sub) => {
              const subObj = { ...sub };
              if (subObj.value !== item.value) {
                subObj.selected = false;
              } else {
                subObj.selected = !subObj.selected;
              }
              return subObj;
            });
            rObj.subItems = sI;
          }
        }
        return rObj;
      });
      this.filterAvailabiltyItem = nd;
      this.filterProducts();
    },
    onPotencySelected(range) {
      this.filterPotency = range;
      this.filterProducts();
    },
    filterProducts(item = null) {
      this.currentPage = 1;
      this.filteredProducts = this.applyCategoryFilter(this.products);
      this.filteredProducts = this.applyBrandsFilter(this.filteredProducts);
      this.filteredProducts = this.applyFeaturedFilter(this.filteredProducts);
      // this.filteredProducts = this.applyProductTypeFilter(this.filteredProducts);
      this.filteredProducts = this.applyStrainsFilter(this.filteredProducts);
      this.filteredProducts = this.applyPotencyFilter(this.filteredProducts);
      this.filteredProducts = this.applyCannabinoidFilter(this.filteredProducts);
      if (item) {
        this.filteredProducts = this.applyInputPriceRangeFilter(this.filteredProducts, item);
      } else {
        this.filteredProducts = this.applyPriceRangeFilter(this.filteredProducts);
      }
      this.filteredProducts = this.applyPreOrderFilter(this.filteredProducts);
      this.filteredProducts = this.applyPreOrderSubFilter(this.filteredProducts);
      this.filteredProducts = this.applyIndustryFilter(this.filteredProducts);
      this.sortProducts();
    },
    applyCategoryFilter(products) {
      const productSets = [];
      if (!this.filterCategoryItems.some((item) => item.selected)) {
        this.buildStrainsFilterItems(products);
        this.buildBrandsFilterItems(products);
        this.buildCannabinoidsFilterItems(products);
        this.buildPriceRangesFilterItems(products);
        return products;
      }

      // does it this way to prevent cascading of filter and applies each filter to its own product set and then combines them
      // which prevents cascading of filters
      const selectedCategories = this.filterCategoryItems.filter((cat) => cat.selected);
      selectedCategories.forEach((category) => {
        if (category.selected) {
          const categoryProductSet = products.filter((p) => p.category_name.toLowerCase() == category.value.toLowerCase());
          productSets.push(this.applyProductTypeFilter(categoryProductSet, category.types));
        }
      });

      const filtredProducts = productSets.reduce((a, b) => a.concat(b), []);

      this.buildStrainsFilterItems(filtredProducts);
      this.buildBrandsFilterItems(filtredProducts);
      this.buildCannabinoidsFilterItems(filtredProducts);
      this.buildPriceRangesFilterItems(filtredProducts);
      return filtredProducts;
    },
    applyProductTypeFilter(categoryProducts, categoryProductTypes) {
      const productSets = [];
      if (!categoryProductTypes.some((item) => item.selected)) {
        return categoryProducts;
      }

      const selectedProductTypes = categoryProductTypes.filter((cat) => cat.selected);
      selectedProductTypes.forEach((productType) => {
        if (productType.selected) {
          const productTypeProductSet = categoryProducts.filter((p) => p.product_type.toLowerCase() == productType.value.toLowerCase());
          productSets.push(productTypeProductSet);
        }
      });

      return productSets.reduce((a, b) => a.concat(b), []);
    },
    applyStrainsFilter(products) {
      const selected = this.filterStrainsItems.find((pt) => pt.selected == true);
      if (selected) {
        return products.filter((prod) => prod.cultivar_type == selected.value);
      }

      return products;
    },
    applyBrandsFilter(products) {
      const selected = this.filterBrandsItems.find((pt) => pt.selected == true);
      if (selected) {
        return products.filter((prod) => prod.brand && prod.brand.name == selected.value);
      }

      return products;
    },
    applyIndustryFilter(products) {
      const selected = this.filterIndustryItems.find((pt) => pt.selected == true);
      if (selected) {
        return products.filter((prod) => {
          for (let i = 0; i < prod.batches.length; i += 1) {
            if (prod.batches[i].operation && prod.batches[i].operation.industry == selected.value) {
              return true;
            }
          }
          return false;
        });
      }

      return products;
    },
    applyFeaturedFilter(products) {
      const selected = this.filterFeaturedItems.find((pt) => pt.selected == true);
      if (selected) {
        return products.filter((prod) => prod.featured);
      }

      return products;
    },
    applyPotencyFilter(products) {
      if (!this.filterPotency || !this.filterPotency.unit) {
        return products;
      }
      const filteredPotencyPercentProducts = products.filter((prod) => {
        if (prod.batches[0].predominate_canabinoid_unit == this.filterPotency.unit) {
          // check for a potency range.
          if (prod.batches[0].predominate_canabinoid_max) {
            // check if the potency range is within the filter range.
            if (
              prod.batches[0].predominate_canabinoid_max >= this.filterPotency.min &&
              prod.batches[0].predominate_canabinoid_max <= this.filterPotency.max &&
              prod.batches[0].predominate_canabinoid_min_or_only <= this.filterPotency.max
            ) {
              return true;
            }
            if (
              prod.batches[0].predominate_canabinoid_max >= this.filterPotency.min &&
              prod.batches[0].predominate_canabinoid_min_or_only >= this.filterPotency.min &&
              prod.batches[0].predominate_canabinoid_min_or_only <= this.filterPotency.max
            ) {
              return true;
            }
          } else if (!prod.batches[0].predominate_canabinoid_max && prod.batches[0].predominate_canabinoid_min_or_only) {
            // check if the potency is within the filter range.
            if (
              prod.batches[0].predominate_canabinoid_min_or_only >= this.filterPotency.min &&
              prod.batches[0].predominate_canabinoid_min_or_only <= this.filterPotency.max
            ) {
              return true;
            }
          } else {
            return false;
          }
        }
        return false;
      });

      return filteredPotencyPercentProducts;
    },
    applyCannabinoidFilter(products) {
      const selected = this.filterCannabinoidsItems.find((pt) => pt.selected == true);
      if (selected) {
        return products.filter((prod) => prod.predominateCanabinoidAbbreviation && prod.predominateCanabinoidAbbreviation == selected.value);
      }

      return products;
    },
    applyPriceRangeFilter(products) {
      const selected = this.filterPriceRangesItems.find((pt) => pt.selected == true);
      if (selected) {
        let [min, max] = selected.value;
        if (!min) {
          min = 0;
        }
        if (!max) {
          max = this.getMaxPriceHelper(products);
        }

        return products.filter((prod) => prod.listing_price / 100 >= min && prod.listing_price / 100 <= max);
      }

      return products;
    },
    applyInputPriceRangeFilter(products, item) {
      const selected = item;
      if (selected) {
        let [min, max] = selected.value;
        if (!min) {
          min = 0;
        }
        if (!max) {
          max = this.getMaxPriceHelper(products);
        }

        return products.filter((prod) => prod.listing_price / 100 >= min && prod.listing_price / 100 <= max);
      }

      return products;
    },
    applyPreOrderFilter(products) {
      const selected = this.filterAvailabiltyItem.find((pt) => pt.selected == true);
      if (selected) {
        if (selected.value == "in-stock") {
          return products.filter((prod) => {
            if (prod.batches && prod.batches.length > 0) {
              if (prod.batches.some((batch) => !batch.back_order)) {
                return true;
              }

              return false;
            }
            return false;
          });
        }
        if (selected.value == "Pre Order") {
          return products.filter((prod) => {
            if (prod.batches && prod.batches.length > 0) {
              if (prod.batches.some((batch) => batch.back_order)) {
                return true;
              }

              return false;
            }
            return false;
          });
        }
      }
      return products;
    },
    applyPreOrderSubFilter(products) {
      const selected = this.filterAvailabiltyItem.find((pt) => pt.selected == true);
      if (selected) {
        if (selected.value == "Pre Order") {
          const selectedSubItem = selected.subItems.find((subItem) => subItem.selected);
          if (selectedSubItem) {
            if (selectedSubItem.value == "Available within 1 Week") {
              return products.filter((prod) => {
                if (prod.batches && prod.batches.length > 0) {
                  if (
                    prod.batches.some(
                      (batch) =>
                        batch.back_order && batch.in_stock_expected_date && dayjs(batch.in_stock_expected_date).isBefore(dayjs().add(1, "w")),
                    )
                  ) {
                    return true;
                  }

                  return false;
                }
                return false;
              });
            }
            if (selectedSubItem.value == "Available within 2 Weeks") {
              return products.filter((prod) => {
                if (prod.batches && prod.batches.length > 0) {
                  if (
                    prod.batches.some(
                      (batch) =>
                        batch.back_order && batch.in_stock_expected_date && dayjs(batch.in_stock_expected_date).isBefore(dayjs().add(2, "w")),
                    )
                  ) {
                    return true;
                  }

                  return false;
                }
                return false;
              });
            }
            if (selectedSubItem.value == "Available within 1 Month") {
              return products.filter((prod) => {
                if (prod.batches && prod.batches.length > 0) {
                  if (
                    prod.batches.some(
                      (batch) =>
                        batch.back_order && batch.in_stock_expected_date && dayjs(batch.in_stock_expected_date).isBefore(dayjs().add(1, "M")),
                    )
                  ) {
                    return true;
                  }

                  return false;
                }
                return false;
              });
            }
            if (selectedSubItem.value == "Available within 3 Month") {
              return products.filter((prod) => {
                if (prod.batches && prod.batches.length > 0) {
                  if (
                    prod.batches.some(
                      (batch) =>
                        batch.back_order && batch.in_stock_expected_date && dayjs(batch.in_stock_expected_date).isBefore(dayjs().add(3, "M")),
                    )
                  ) {
                    return true;
                  }

                  return false;
                }
                return false;
              });
            }
          }
        }
      }

      return products;
    },
    launchPTOModal(data) {
      this.ptoProduct = { ...data.product };
      this.ptoBatch = { ...data.batch };
      this.ptoModalKey += 1;
      this.isShowingPTOModal = true;
    },
    createPackToOrderItems(ptoObject) {
      this.ptoModalKey += 1;
      const data = { cartItems: [ptoObject] };
      this.$emit("add-to-cart", data);
    },
    handleLaunchingPTOModalFromPDPModal(val) {
      this.hideProductDisplayPageModal();
      this.launchPTOModal({ product: val.product, batch: val.batch });
    },
    onPageResize() {
      window.onresize = function (e) {
        this.showSidebar = window.innerWidth > 992;
        this.sidebarWidth = window.innerWidth > 992 ? "300px" : "100%";
        this.sidebarKey += 1;
        return false;
      };
    },
    switchPage(page) {
      this.currentPage = page;
    },
    handleLoadedProducts(newProducts) {
      this.filteredProducts = clonedeep(newProducts);
      this.sortBy(this.selectedSortBy);
      this.filterProducts();
    },
    switchToGrid() {
      this.filters = [];
      this.filtered = false;
      this.defaultLayout = "grid";
      this.$emit("view-switched", this.defaultLayout);
    },
    switchToTable() {
      this.filters = [];
      this.filtered = false;
      this.defaultLayout = "table";
      this.$emit("view-switched", this.defaultLayout);
    },
    sortBy(selected) {
      this.selectedSortBy = null; // need to clear any previous sorts. simple and ugly.
      this.selectedSortBy = selected;
      this.sortProducts();
    },
    sortProducts() {
      if (this.selectedSortBy && this.selectedSortBy.name) {
        if (this.selectedSortBy.name == "Category") {
          this.sortByCategory();
        }
        if (this.selectedSortBy.name == "Price Low") {
          this.sortByPriceLow();
        }
        if (this.selectedSortBy.name == "Price High") {
          this.sortByPriceHigh();
        }
        if (this.selectedSortBy.name == "Name") {
          this.sortByName();
        }
        if (this.selectedSortBy.name == "Brand") {
          this.sortByBrand();
        }
        if (this.selectedSortBy.name == "Strain") {
          this.sortByStrain();
        }
      }
    },
    sortByPriceLow() {
      this.filteredProducts = this.filteredProducts.sort((a, b) => {
        const lastA = a.tiered_listing_price ? a.tiered_listing_price : a.listing_price;
        const lastB = b.tiered_listing_price ? b.tiered_listing_price : b.listing_price;
        if (Math.abs(lastA) >= Math.abs(lastB)) {
          return 1;
        }
        return -1;
      });
    },
    sortByCategory() {
      this.filteredProducts = this.filteredProducts.sort((a, b) => {
        if (a.category_id >= b.category_id) {
          return 1;
        }
        return -1;
      });
    },
    sortByPriceHigh() {
      this.filteredProducts = this.filteredProducts.sort((a, b) => {
        const lastA = a.tiered_listing_price ? a.tiered_listing_price : a.listing_price;
        const lastB = b.tiered_listing_price ? b.tiered_listing_price : b.listing_price;
        if (Math.abs(lastA) <= Math.abs(lastB)) {
          return 1;
        }
        return -1;
      });
    },
    sortByName() {
      this.filteredProducts = this.filteredProducts.sort((a, b) => {
        if (a.product_name > b.product_name) {
          return 1;
        }
        return -1;
      });
    },
    sortByBrand() {
      this.filteredProducts = this.filteredProducts.sort((a, b) => {
        if (a.brand && b.brand && a.brand.name > b.brand.name) {
          return 1;
        }
        return -1;
      });
    },
    sortByStrain() {
      this.filteredProducts = this.filteredProducts.sort((a, b) => {
        if (a.cultivar_type > b.cultivar_type) {
          return 1;
        }
        return -1;
      });
    },
    pdpContactClicked() {
      this.hideProductDisplayPageModal();
      this.$emit("show-contact-sales", true);
    },
    hideProductDisplayPageModal() {
      this.productCurrentlyShowingBulkDiscounts = [];
      this.showProductModal = false;
      this.selectedBatchId = null;
    },
    launchProductDisplayPageModal(selectedProduct) {
      const product = selectedProduct;
      this.productDisplayPageModalKey += 1;
      this.selectedProduct = product;
      if (product.batches && product.batches.length == 1) {
        this.selectedBatchId = product.batches[0].id;
      }
      this.productCurrentlyShowingBulkDiscounts = this.bulkDiscounts.filter((discount) => {
        if (product.product_type_id) {
          return discount.product_type_id == product.type_id;
        }
        return discount.product_category_id == product.category_id;
      });
      this.showProductModal = true;
      // this.productCurrentlyShowingBulkDiscounts = ['one', 'two', 'test'];
    },
    addProductFromDisplayPage(data) {
      const item = {
        batch_id: data.batch_id,
        quantity: data.quantity,
        sample: data.sample,
        product_id: data.product_id,
        company_id: data.company_id,
      };
      const payload = { cartItems: [item] };
      this.$emit("add-to-cart", payload);
    },
    handleFilter(filter) {
      if (filter.length > 0 && filter[0].value) {
        if (this.filters.some((storedFilter) => storedFilter.name == filter[0].title)) {
          this.filters = this.filters.filter((storedFilter) => storedFilter.name !== filter[0].title);
        }
        this.filters.push({
          name: filter[0].title,
          filter,
        });
        this.filterProducts();
      } else if (filter.title) {
        if (filter.title == "Categories") {
          this.selectedCategory = "All Products";
        }
        this.filters = this.filters.filter((storedFilter) => storedFilter.name !== filter.title);

        if (this.filters.length > 0) {
          this.filterProducts();
        } else {
          this.filtered = false;
          this.filteredProducts = clonedeep(this.products);
          this.sortProducts();
        }
      } else {
        this.filtered = false;
        this.filteredProducts = this.products;
        this.sortProducts();
      }
    },

    createOrder(order) {
      this.$emit("create-order", order);
    },
  },
};
</script>

<style lang="scss" scoped></style>

<style lang="css" scoped>
.grid {
  opacity: 0;
  transition: opacity 2s;
  height: 0;
  width: 0;
  overflow: hidden;
  position: fixed;
}

.table-layout {
  opacity: 0;
  transition: opacity 2s;
  height: 0px;
  width: 0px;
  overflow: hidden;
  position: fixed;
}

.show-grid {
  opacity: 1;
  height: auto;
  width: auto;
  position: relative;
}

.show-table-layout {
  opacity: 1;
  height: auto;
  width: auto;
  position: relative !important;
}

:deep(.bg-warning-light) {
  background: rgb(255, 244, 180);
}
</style>
